import { DeclareMethod } from '/imports/methods';

export interface TestSmsArgs {
  to: string;
  from: string;
  body: string;
}

export interface TestSmsResult {
  ok: boolean;
  sid?: string;
  status?: string;
  error?: string;
}

export const runTestSms = DeclareMethod<TestSmsArgs, TestSmsResult>('runTestSms');
export const getTwilioNumbers = DeclareMethod<void, { numberLive?: string; numberOpen?: string }>('getTwilioNumbers');

/** `sets`: optional comma-separated set codes ("MH3, OTJ") to scrape just
 *  those; empty scrapes every scheduled set. Returns what will be scraped. */
export const runGoatbotsJob = DeclareMethod<{ sets?: string } | void, { jobId: string; sets: string[] }>('runGoatbotsJob');

/** Refresh the metagame CardLists from MTGGoldfish. `lists` is what the job
 *  will actually touch — empty when no card list is named after a metagame,
 *  which is the job's do-nothing case and worth showing before it runs. */
export const runMtgoGoldfishJob = DeclareMethod<void, { jobId: string; lists: string[] }>('runMtgoGoldfishJob');

export interface ImagesArgs {
  // Comma-separated MTGO set codes; blank/omitted = all sets.
  setCodes?: string;
  // When false (default), only items missing the asset are touched; true overwrites.
  force?: boolean;
  // Which steps to run (all default on in the UI). Card/artwork thumbnails moved
  // to the ingest tool (runIngestImages) since they source the ingested fields.
  cards?: boolean;       // card images (Scryfall cascade + MTGO art floor)
  setIcons?: boolean;    // set symbols (Scryfall /sets) -> sets.image
  boosters?: boolean;    // booster/product images (MTGO Store2 CDN)
}
// Unified image pipeline: one job runs the selected steps in sequence over the
// given sets (see server/integrations/imagePipeline.ts).
export const runImages = DeclareMethod<ImagesArgs, { jobId: string }>('runImages');

export interface IngestImagesArgs {
  // Comma-separated MTGO set codes; blank/omitted = all sets.
  setCodes?: string;
  // When false (default), skip cards that already have the field; true overwrites.
  force?: boolean;
  // Which source dirs to ingest (all default on in the UI):
  cc?: boolean;   // /nfs/cardImages/cc -> Card.mtgoImage (MTGO client full card)
  sc?: boolean;   // /nfs/cardImages/sc -> Card.scryfallImage (Scryfall full card)
  ca?: boolean;   // /nfs/cardImages/ca -> Card.mtgoArtwork (MTGO client art)
  sa?: boolean;   // /nfs/cardImages/sa -> Card.scryfallArtwork (Scryfall art)
  // Scrape set symbols (Scryfall /sets) -> sets.image, same as the download
  // pipeline's step.
  setIcons?: boolean;
  // Generate thumbnails after ingesting (tc/ta -> imageThumb/artworkThumb),
  // sourced Scryfall-preferred with MTGO fallback.
  thumbs?: boolean;
}
// Ingest locally-generated card images/artwork into Card FileMeta fields, mapped
// by numeric filename (= Card._id). See server/integrations/cardImageIngest.ts.
export const runIngestImages = DeclareMethod<IngestImagesArgs, { jobId: string }>('runIngestImages');

export interface GenerateOtpResult {
  ok: boolean;
  otp?: string;
  expiresInSec?: number;
  error?: string;
}
export const generateOtpFromMfaKey = DeclareMethod<{ mfaKey: string }, GenerateOtpResult>('generateOtpFromMfaKey');

export interface ExtractChecksumResult {
  ok: boolean;
  md5?: string;       // the 32-char client checksum extracted from the DLL
  version?: string;   // FileVersion string, e.g. '3.4.157.4677'
  current?: string;   // the checksum the bot currently sends at login (Settings.md5)
  changed?: boolean;  // md5 differs from `current`
  error?: string;
}
// Extract the MTGO login checksum from an uploaded FlsClient.dll (base64). The
// md5 lives in the .NET #US string heap as a UTF-16 32-char hex string; a client
// update changes it and the server rejects logins (ClientMd5SumIsInvalid). See
// server/integrations/clientChecksum.ts.
export const extractChecksum = DeclareMethod<{ dllBase64: string }, ExtractChecksumResult>('extractChecksum');

export interface ApplyChecksumResult { ok: boolean; applied?: boolean; error?: string }
// Write the extracted checksum to Settings.md5, which Packet.login reads on
// every bot login. The client build version is shown in the UI for context but
// is not stored -- there is only ever one live checksum.
export const applyChecksum = DeclareMethod<{ md5: string }, ApplyChecksumResult>('applyChecksum');

// --- Registry opportunity analysis (read-only) ------------------------------
export interface RegistrySellOpp { cid: number; name: string; uid: number; theirBid: number; ourExit: number; margin: number }
export interface RegistryArb { cid: number; name: string; buyUid: number; sellUid: number; ask: number; bid: number; spread: number }
export interface RegistryReport {
  quotesConsidered: number;
  distinctCards: number;
  distinctChains: number;
  sellOpportunities: RegistrySellOpp[];
  arbitrage: RegistryArb[];
  missBuckets: { label: string; count: number }[];
  /** The LIVE shopping list currently driving inventory (margin >= cutoff). */
  shoppingList: { cid: number; name: string; margin: number; theirBid: number; ourExit: number; uid: number }[];
}
/** Read-only opportunity analysis over the chainQuotes registry: does the
 *  bot-to-bot channel have any money in it? `maxAgeDays` bounds staleness. */
export const analyzeRegistry = DeclareMethod<{ maxAgeDays?: number } | void, RegistryReport>('analyzeRegistry');

// --- Ad-price analysis (read-only) ------------------------------------------
export interface AdDivergenceRow {
  cid: number; name: string; ourBuy: number | null; ourSell: number | null;
  adConsensus: number; adLow: number; adHigh: number; chains: number;
  direction: 'below' | 'above'; factor: number;
}
export interface AdPriceReport {
  advertisers: number;
  advertisersWithPrices: number;
  pricedLines: number;
  resolution: { unique: number; uniquePriced: number; ambiguous: number; unresolved: number };
  comparableCids: number;
  divergences: AdDivergenceRow[];
  ambiguousSamples: { name: string; printings: number; ourRange: string }[];
}
/** Read-only proof slice: parse external bots' classified ads as a price feed
 *  and measure how much is cleanly resolvable (name -> single priced printing)
 *  vs. lost to printing/foil ambiguity, then flag where the ad consensus sits
 *  far outside our own book (a stale-price signal). Wired into NO trade path. */
export const analyzeAdPrices = DeclareMethod<{ minChains?: number } | void, AdPriceReport>('analyzeAdPrices');

// --- Our store-credit at external chains (read-only) ------------------------
export interface ChainCreditRowOut { botUsername: string; partnerUid: number; partnerName: string | null; credit: number; kind: string; at: Date }
export interface ChainCreditFamilyOut { botUsername: string; family: string; credit: number; members: number; latestAt: Date }
export interface ChainCreditReport {
  rows: ChainCreditRowOut[];
  /** Per (our bot, chain family): latest statement, never summed across siblings. */
  families: ChainCreditFamilyOut[];
  /** Sum of family figures -- a floor, since non-sharing chains are under-counted. */
  atLeastTotal: number;
}
export const listChainCredits = DeclareMethod<void, ChainCreditReport>('listChainCredits');
